import { createFileRoute, Link } from "@tanstack/react-router";
import { useLocale } from "@/lib/i18n/locale-context";
import { useQuery } from "@tanstack/react-query";
import {
  User,
  Receipt,
  Package,
  BarChart3,
  MapPin,
  Sparkles,
  ArrowRight,
  Calendar,
  CreditCard,
  ShieldCheck,
  LayoutDashboard,
} from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { myPlanQuery } from "@/lib/queries";
import { PLANS, UNLIMITED_LIMITS, type PlanId, type AdUsage } from "@/lib/plans";
import { Button } from "@/components/ui/button";
import { NotificationSettings } from "@/components/NotificationSettings";
import { PushSettings } from "@/components/PushSettings";
import { AppLockSettings } from "@/app-bridge/components/AppLockSettings";
import { PrivacyChoicesSection } from "@/components/PrivacyChoicesSection";
import { SignInMethod } from "@/components/SignInMethod";
import { DangerZone } from "@/components/DangerZone";
import { BusinessProfileSection } from "@/components/BusinessProfileSection";
import { useIsAdmin } from "@/components/AdminAdInfo";
import { AppDebugPanel } from "@/app-bridge/components/AppDebugPanel";
import { BusinessTestPanel } from "@/components/admin/BusinessTestPanel";
import { AccountDevicesSection } from "@/components/AccountDevicesSection";
import { AdminPlanModeCard } from "@/components/admin/AdminPlanModeCard";


export const Route = createFileRoute("/_authenticated/konto")({
  head: () => ({
    meta: [
      { title: "Mitt konto — Sellavo" },
      {
        name: "description",
        content: "Hantera ditt konto, abonnemang, kvitton och gränser på Sellavo.",
      },
      { property: "og:title", content: "Mitt konto — Sellavo" },
      { property: "og:description", content: "Ditt konto och abonnemang på Sellavo." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex" },
    ],
    links: [{ rel: "canonical", href: "https://sellavo.com/konto" }],
  }),
  loader: ({ context }) => context.queryClient.ensureQueryData(myPlanQuery()),
  component: AccountPage,
});

function formatDay(value?: string | null): string | null {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("sv-SE", { year: "numeric", month: "long", day: "numeric" });
}

function AccountPage() {
  const { lang } = useLocale();
  const { user } = useAuth();
  const isAdmin = useIsAdmin();
  const { data: planStatus } = useQuery(myPlanQuery());

  const currentPlan: PlanId = (planStatus?.plan as PlanId) ?? "free";
  const plan = PLANS[currentPlan];
  const unlimited = planStatus?.unlimited === true;
  const fromRole = planStatus?.fromRole === true;
  const isFree = currentPlan === "free";
  const usage: AdUsage = planStatus?.usage ?? { day: 0, week: 0, month: 0 };
  const limits = unlimited ? UNLIMITED_LIMITS : plan.limits;

  const periodDays =
    planStatus?.startedAt && planStatus?.currentPeriodEnd
      ? (new Date(planStatus.currentPeriodEnd).getTime() -
          new Date(planStatus.startedAt).getTime()) /
        86400000
      : null;
  const billingInterval = periodDays !== null && periodDays > 200 ? "Årsvis" : "Månadsvis";
  const memberSince = planStatus?.startedAt ?? user?.created_at ?? null;

  return (
    <main className="mx-auto w-full max-w-5xl px-4 sm:px-6 lg:px-8 pb-16 pt-6 sm:pb-20 sm:pt-10">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="font-heading text-2xl font-extrabold tracking-tight text-foreground sm:text-4xl">
            Mitt konto
          </h1>
          <p className="mt-1 text-sm text-muted-foreground sm:mt-2 sm:text-base">
            Hantera abonnemang, kvitton och gränser.
          </p>
        </div>
      </div>

      <section className="mt-5 rounded-xl border border-border bg-card p-4 shadow-sm sm:mt-8 sm:p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
              <User className="h-6 w-6" />
            </div>
            <div>
              <p className="font-heading text-lg font-semibold text-foreground">{user?.email}</p>
              <p className="text-sm text-muted-foreground">
                Medlem sedan {formatDay(memberSince) ?? "—"}
              </p>
              <SignInMethod className="mt-2" />
            </div>
          </div>
          <span className="flex flex-wrap items-center justify-end gap-2">
            <span className="rounded-full bg-primary/10 px-3 py-1 text-sm font-semibold text-primary">
              {plan.name}
            </span>
            {fromRole && (
              <span className="rounded-full bg-amber-500/15 px-3 py-1 text-sm font-semibold text-amber-600">
                Ingår i din adminroll
              </span>
            )}
          </span>
        </div>

        <div className="mt-5 grid grid-cols-2 gap-4 lg:grid-cols-4">
          <Detail icon={<CreditCard className="h-4 w-4" />} label="Nivå" value={plan.name} />
          {fromRole ? (
            <Detail
              icon={<Calendar className="h-4 w-4" />}
              label="Betalning"
              value="Ingen – adminroll"
            />
          ) : (
            !isFree && (
              <Detail
                icon={<Calendar className="h-4 w-4" />}
                label="Betalning"
                value={billingInterval}
              />
            )
          )}
          {isFree || fromRole ? null : (
            <Detail
              icon={<Calendar className="h-4 w-4" />}
              label="Startade"
              value={formatDay(planStatus?.startedAt ?? memberSince) ?? "—"}
            />
          )}

          {!isFree && (
            <Detail
              icon={<Calendar className="h-4 w-4" />}
              label="Förnyas / gäller till"
              value={
                fromRole
                  ? "Tills vidare"
                  : (formatDay(planStatus?.currentPeriodEnd) ?? "Tills vidare")
              }
            />
          )}
        </div>

        {fromRole ? (
          <p className="mt-6 text-sm text-muted-foreground">
            Du har högsta nivån ({plan.name}) utan kostnad och utan några gränser, eftersom ditt
            konto är administratör. Alla funktioner är öppna.
          </p>
        ) : isFree ? (
          <div className="mt-6 rounded-lg border border-dashed border-border bg-muted/40 p-4">
            <p className="text-sm text-muted-foreground">
              Du har gratiskontot. Det kostar ingenting och du kan lägga ut upp till{" "}
              {plan.limits.perMonth} annonser per månad. Vill du ha fler annonser, fler bilder per
              annons eller video kan du uppgradera när som helst.
            </p>
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <Button asChild>
                <Link to="/priser">Jämför konton</Link>
              </Button>
              <Link to="/min-niva" className="text-sm font-semibold text-primary hover:underline">
                Se vad som ingår i din nivå
              </Link>
            </div>
          </div>
        ) : (
          <p className="mt-6 text-sm text-muted-foreground">
            Du betalar {billingInterval.toLowerCase()}. Alla priser är inklusive moms (25 %). Kvitto
            skickas till din e-post vid varje betalning och kommer även att gå att ladda ner här som
            PDF när betalningarna är igång.{" "}
            <Link to="/min-niva" className="font-medium text-primary hover:underline">
              Se vad som ingår i din nivå
            </Link>{" "}
            ·{" "}
            <Link to="/priser" className="font-medium text-primary hover:underline">
              Byt nivå
            </Link>
          </p>
        )}
      </section>

      {(isAdmin || planStatus?.isAdminAccount) && (
        <AdminPlanModeCard mode={planStatus?.adminMode as never} />
      )}



      <section className="mt-5 rounded-xl border border-border bg-card p-4 shadow-sm sm:mt-8 sm:p-6">
        <h2 className="flex items-center gap-2 font-heading text-lg font-semibold text-foreground">
          <BarChart3 className="h-5 w-5 text-primary" /> Användning den här perioden
        </h2>
        <div className="mt-6 space-y-5">
          <UsageBar
            label="Annonser idag"
            used={usage.day}
            limit={limits.perDay}
            hint={limits.perDay === null ? "Ingen fast daglig gräns" : undefined}
          />
          <UsageBar
            label="Annonser den här veckan"
            used={usage.week}
            limit={limits.perWeek}
            hint={limits.perWeek === null ? "Ingen fast veckogräns" : undefined}
          />
          <UsageBar
            label="Annonser den här månaden"
            used={usage.month}
            limit={limits.perMonth}
            hint={limits.perMonth === null ? "Obegränsat antal annonser" : undefined}
          />
        </div>
        {planStatus?.quota?.allowed === false && (
          <p className="mt-5 rounded-md border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-600">
            {planStatus.quota.reason}
          </p>
        )}
      </section>

      <section className="mt-5 rounded-xl border border-border bg-card p-4 shadow-sm sm:mt-8 sm:p-6">
        <h2 className="flex items-center gap-2 font-heading text-lg font-semibold text-foreground">
          <Receipt className="h-5 w-5 text-primary" /> Kvitton och betalning
        </h2>
        {isFree ? (
          <p className="mt-3 text-sm text-muted-foreground">
            Med gratiskontot finns inga kvitton. Så fort du uppgraderar till Plus eller Pro visas
            dina betalningar här.
          </p>
        ) : (
          <>
            <p className="mt-3 text-sm text-muted-foreground">
              Dina kvitton och betalningshistorik kommer att visas här så snart betalningarna är
              igång.
            </p>
            <Button variant="outline" className="mt-4" disabled>
              Hämta kvitton
            </Button>
          </>
        )}
      </section>

      <section className="mt-5 grid gap-3 sm:mt-8 sm:gap-4 sm:grid-cols-2">
        <ActionCard
          icon={<LayoutDashboard className="h-5 w-5" />}
          title="Min översikt"
          description="Visningar, kontakter och vad som behöver din uppmärksamhet."
          to="/oversikt"
        />
        <ActionCard
          icon={<Package className="h-5 w-5" />}
          title="Mina annonser"
          description="Redigera, pausa, förnya eller arkivera dina annonser."
          to="/mina-annonser"
        />
        <ActionCard
          icon={<MapPin className="h-5 w-5" />}
          title="Mina adresser"
          description="Hantera sparade platser för dina annonser."
          to="/adresser"
        />
        <ActionCard
          icon={<BarChart3 className="h-5 w-5" />}
          title="Statistik"
          description="Se hur dina annonser går och få tips om prisjustering."
          to="/statistik"
        />
        <ActionCard
          icon={<Sparkles className="h-5 w-5" />}
          title="Min profil"
          description="Visningsnamn, profilbild och telefonnummer."
          to="/profil"
        />
      </section>

      <BusinessProfileSection />
      <Link
        to="/foretagsverktyg"
        className="block rounded-xl border border-border bg-card p-4 text-sm font-medium transition hover:border-primary/50"
      >
        {lang === "en" ? "Business tools – statistics, bulk editing, import, staff, opening hours →" : lang === "es" ? "Herramientas de empresa – estadísticas, edición masiva, importación, empleados, horario →" : "Företagsverktyg – statistik, masshantering, import, anställda, öppettider →"}
      </Link>

      <NotificationSettings />
      <PushSettings />
      <AppLockSettings />
      <PrivacyChoicesSection />

      {isAdmin && <AppDebugPanel />}
      {isAdmin && <BusinessTestPanel />}

      <AccountDevicesSection />

      <DangerZone />

      <div className="mt-5 flex items-start gap-3 sm:mt-8 rounded-lg border border-border bg-muted/40 p-4 text-sm text-muted-foreground">
        <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
        <p>
          Du kan byta nivå eller säga upp abonnemanget när som helst. Ingen bindningstid, inga dolda
          avgifter och ingen provision på det du säljer.
        </p>
      </div>
    </main>
  );
}

function Detail({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div>
      <dt className="flex items-center gap-1.5 text-xs uppercase tracking-wide text-muted-foreground">
        {icon} {label}
      </dt>
      <dd className="mt-1 font-heading text-base font-semibold text-foreground">{value}</dd>
    </div>
  );
}

function UsageBar({
  label,
  used,
  limit,
  hint,
}: {
  label: string;
  used: number;
  limit: number | null;
  hint?: string;
}) {
  const unlimited = limit === null;
  const pct = unlimited ? 0 : Math.max(0, Math.min(100, (used / Math.max(1, limit)) * 100));
  const remaining = unlimited ? null : Math.max(0, (limit ?? 0) - used);

  return (
    <div>
      <div className="flex items-center justify-between text-sm">
        <span className="font-medium text-foreground">{label}</span>
        <span className="text-muted-foreground">
          {unlimited ? `${used} använda` : `${used} / ${limit} ${hint ? `(${hint})` : ""}`}
          {remaining !== null && remaining > 0 && !unlimited && ` · ${remaining} kvar`}
        </span>
      </div>
      {!unlimited && (
        <div className="mt-2 h-2 overflow-hidden rounded-full bg-muted">
          <div
            className={`h-full rounded-full ${pct >= 90 ? "bg-amber-500" : "bg-primary"}`}
            style={{ width: `${pct}%` }}
          />
        </div>
      )}
    </div>
  );
}

function ActionCard({
  icon,
  title,
  description,
  to,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  to: string;
}) {
  return (
    <Link
      to={to as never}
      className="group flex items-center gap-3 rounded-lg border border-border bg-card p-4 transition-colors hover:border-primary/50 hover:bg-card/80 active:bg-muted sm:flex-col sm:items-stretch sm:p-5"
    >
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
        {icon}
      </div>
      <div className="min-w-0 flex-1 sm:mt-4">
        <p className="font-heading font-semibold text-foreground">{title}</p>
        <p className="mt-0.5 hidden text-sm text-muted-foreground sm:mt-1 sm:block">
          {description}
        </p>
      </div>
      <ArrowRight className="h-5 w-5 shrink-0 text-muted-foreground transition-colors group-hover:text-primary sm:hidden" />
    </Link>
  );
}
